import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Product, ProductsService } from '@ubie/core';

type SelectedProduct = Product & { isSelected: boolean };

@Component({
  selector: 'acl-installation-payment',
  standalone: false,
  templateUrl: './installation-payment.component.html',
  styleUrl: './installation-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationPaymentComponent implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly modalController = inject(ModalController);

  // products that can be bought through online payment via mollie
  readonly productsToBuyWithPaymentProducts = signal<SelectedProduct[]>([]);

  readonly originId = input<number>(1);

  readonly filteredProducts = computed(() =>
    this.productsToBuyWithPaymentProducts().filter((p) =>
      this.originId() === 2
        ? p.name.startsWith('products_qcp')
        : p.name.startsWith('products_qc') &&
          !p.name.startsWith('products_qcp'),
    ),
  );

  readonly showUnlimited = computed(() => {
    const date = this.installerAccessUntil();
    if (date) {
      const minUnlimitedDate = new Date();
      minUnlimitedDate.setFullYear(minUnlimitedDate.getFullYear() + 100);

      if (minUnlimitedDate < new Date(date)) {
        return true;
      }
      return false;
    }
    return false;
  });

  @Input() set reload(isReload: boolean) {
    if (isReload) {
      this.initComponent();
    }
  }

  // Emit an event when next is clicked (product is null if skipped)
  @Output() readonly licenseStepNext: EventEmitter<Product | null> =
    new EventEmitter<Product | null>();
  @Output() readonly prevClicked = new EventEmitter<void>();

  readonly btnText = input('Continue');
  readonly showPrevButton = input<boolean>(false);
  readonly selectedProduct = model<Product | undefined>(undefined);
  readonly optionsLabel = input<string>('license_options');
  readonly showLicenseStatus = input<boolean>(false);

  readonly installerAccessUntil = input<Date | undefined>(undefined);

  ngOnInit(): void {
    this.initComponent();
  }

  initComponent(): void {
    this.showProductsToBuyWithPaymentProducts();
  }

  /**
   * Get all base products that can be sold by this site
   */
  showProductsToBuyWithPaymentProducts(): void {
    this.productsService.getProductsToBuyWithPaymentProductsBySite().subscribe({
      next: (result) => {
        this.productsToBuyWithPaymentProducts.set(
          result.map((item) => {
            return {
              ...item,
              isSelected: item.uid === this.selectedProduct()?.uid,
            } as SelectedProduct;
          }),
        );
      },
      error: (error) => {
        console.error('could not get products', error);
      },
    });
  }

  /**
   * When a product has been selected
   */
  onProductSelected(product: SelectedProduct): void {
    this.productsToBuyWithPaymentProducts.update((current) => {
      return current.map((item) => {
        return {
          ...item,
          isSelected: item.uid === product.uid,
        };
      });
    });

    this.selectedProduct.set(product);
  }

  submitProduct(): void {
    this.licenseStepNext.emit(this.selectedProduct() ?? null);
  }
}
